import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, HStack, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { buildShareLinkUrl } from '../config';
import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

const COPIED_DURATION_MS = 2000;

// "Sharing Page" opens the public sharing page in a new window; the inset link button
// copies its URL to the clipboard. With `panel`, both sit in a white rounded panel.
const SharingLinkControls = ({ sharingToken, panel = false, size = 'sm' }) => {
  const sharingLinkUrl = useMemo(() => buildShareLinkUrl(sharingToken), [sharingToken]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const copyTimeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    []
  );

  const placement = panel ? 'profile_page' : 'people_list';

  const handleOpen = () => {
    if (sharingLinkUrl) {
      window.open(sharingLinkUrl, '_blank', 'noopener,noreferrer');
      trackEvent(ANALYTICS_EVENTS.SHARING_PAGE_OPENED, { placement });
    }
  };

  const handleCopy = async () => {
    if (!sharingToken) {
      setCopyError('Sharing link unavailable. Please try again later.');
      return;
    }
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(sharingLinkUrl);
      trackEvent(ANALYTICS_EVENTS.SHARING_LINK_COPIED, { placement });
      setCopyError(null);
      setCopySuccess(true);
      copyTimeoutRef.current = setTimeout(() => {
        setCopySuccess(false);
        copyTimeoutRef.current = null;
      }, COPIED_DURATION_MS);
    } catch (err) {
      setCopySuccess(false);
      setCopyError('Unable to copy sharing link. Please try again.');
    }
  };

  const isDisabled = !sharingToken;
  // In the white panel (HoggedIn page): text button plus a light-blue inset copy button.
  // Inline (People list rows): both outlined, matching the row's View Profile button.
  const copyButtonStyle = panel
    ? { bg: 'blue.100', color: 'blue.600', _hover: { bg: 'blue.200' }, borderRadius: 'lg' }
    : { variant: 'outline', colorScheme: 'blue' };

  return (
    <Box>
      <HStack
        spacing={panel ? 1 : 2}
        display="inline-flex"
        {...(panel ? { bg: 'white', borderRadius: 'xl', p: 1, boxShadow: 'sm' } : {})}
      >
        <Button
          size={size}
          variant={panel ? 'ghost' : 'outline'}
          colorScheme="blue"
          leftIcon={<i className="bi bi-box-arrow-up-right" aria-hidden="true" />}
          onClick={handleOpen}
          isDisabled={isDisabled}
          title={sharingLinkUrl ?? undefined}
        >
          Sharing Page
        </Button>
        <Tooltip label={copySuccess ? 'Copied' : 'Copy link'} hasArrow closeOnClick={false}>
          <IconButton
            size={size}
            aria-label="Copy link"
            {...copyButtonStyle}
            icon={<i className={`bi ${copySuccess ? 'bi-check-lg' : 'bi-link-45deg'}`} aria-hidden="true" />}
            onClick={handleCopy}
            isDisabled={isDisabled}
          />
        </Tooltip>
      </HStack>
      {copyError && (
        <Text fontSize="sm" color="red.500" mt={1}>
          {copyError}
        </Text>
      )}
    </Box>
  );
};

export default SharingLinkControls;
